import States from '../../../../helpers/states.js';
import EventEmitter from 'eventemitter3';
import debounce from 'lodash.debounce'
import WallPan from './wallPan';
import WallBloc from './wallBloc';
import CookiesManager from '../../../../helpers/cookies-manager'

/**
 * Infinite wall with drag and drop navigation on x/y
 * @extends EventEmitter
 */
class WallManager extends EventEmitter {

    /**
     * @constructor
     * @param {Node} container
     * @param {Array} datas
     */
    constructor({
                    container,
                    datas
                } = {}) {
        super();

        // dom main container
        this.dom = {
            container: container
        };
        this.datas = datas;

        this.panner = null;

        // dom templates
        // TODO: add to config for wall
        this.dom.templates = {
            bloc: null,
            item: null,
            chef: null,
            header: null,
        }

        // blocs properties
        this.panelOffset = {x: 0, y: 0};
        this._blocSize = {width: States.width, height: States.height};
        this.canUpdateGrid = false;
        this.blocList = new Map();
        this._currentBlocIndex = null;
        this._currentBlocMapIndex = null;
        this._selectedBlocDom = null;
        this.currentBloc = null;

        // grid items
        this.itemSizeRatio = 1.2;
        this.refScreenSize =  {width: 1366, height: 900};
        this.gridSize = {x: 0, y:0};
        this.lastGridSize = {x: 0, y:0};
        this._cellSize = {width: 0, height:0};

        this.init();
    }

    /**
     * Bloc visibility
     * @type {Boolean}
     */
    get active() {
        return document.body.classList.has('wall-active');
    }

    set active(v) {
        document.body.classList[v ? 'add' :'remove']('wall-active');
    }

    /**
     * the current bloc index of wall
     * @type {Number}
     */
    get currentBlocIndex() {
        return this._currentBlocIndex;
    }

    set currentBlocIndex(value) {
        this._currentBlocMapIndex = this._stringifyIndex(value);
        this._currentBlocIndex = value;
    }

    /**
     * Wall cell size
     * @type {{width: Number, height: Number}}
     */
    get cellSize() {
        return this._cellSize;
    }

    set cellSize({width, height}) {
        const sizeRatio = {
            width: States.width / this.refScreenSize.width,
            height: States.height / this.refScreenSize.height
        };
        this._cellSize = {
            width: Math.round(width * this.itemSizeRatio * sizeRatio.width),
            height: Math.round(height * this.itemSizeRatio * sizeRatio.width)
        };

    }

    /**
     * Wall bloc size
     * @type {{width: Number, height: Number}}
     */
    get blocSize() {
        return this._blocSize;
    }

    set blocSize({width, height}) {
        // set grid size once if not already init
        if(this.gridSize.x + this.gridSize.y === 0) {
            // this.gridSize = {
            //     x: Math.ceil( width / this.cellSize.width ),
            //     y: Math.ceil( height / this.cellSize.height )
            // };
            // Grid size as square for resize
            this.gridSize.x = this.gridSize.y = Math.ceil( width / this.cellSize.width );
        }
        this._blocSize = {
            width: this.gridSize.x * this.cellSize.width,
            height: this.gridSize.y * this.cellSize.height
        };
        //this.dom.templates.bloc.style.width = size.width + 'px';

        //this.dom.templates.bloc.style.height = size.height + 'px';
    }

    /**
     * Init the wall,
     * must be init on dom ready
     */
    init() {
        this._retrieveDom();
        this.setSizes();
        this._initPan();
        this._initEvents();
        this._initBlocGrid();
    }

    /**
     * Get dom elements and load js templates
     * @private
     */
    _retrieveDom() {
        this.dom.panel = this.dom.container.querySelector('.panel');
        this.dom.overlay = document.body.querySelector('.wall-overlay');
        this.dom.templateList = document.body.querySelector('.templates');
        this.dom.nav = document.body.querySelector('.menu-head');

        // Tutorial
        this.dom.tutorial = {};
        this.dom.tutorial.container = document.body.querySelector('.wall-tutorial');
        this.dom.tutorial.text = this.dom.tutorial.container.querySelector('.text');
        this.dom.tutorial.icon = this.dom.tutorial.container.querySelector('.icon');
        this.dom.tutorial.iconCircle = document.body.querySelector('.icon-cursor-circle');
        this.dom.tutorial.iconArrows = document.body.querySelector('.icon-cursor-arrows');

        // Templates
        this._loadTemplates();
    }

    /**
     * Init wall pan drag and drop navigation
     * @private
     */
    _initPan() {
        // Init wallPan
        this.panner = new WallPan({
            panel: this.dom.panel,
            overlay: this.dom.overlay,
            offset : this.panelOffset
        });

        // manage grid on drag
        this.panner.on('drag:start', (e) => {
            this._selectedBlocDom = closest(e.target, '.bloc');
            this._manageBlocGrid(this._selectedBlocDom);
        })
    }

    /**
     * Handle window resize
     * @private
     */
    _handleResize = () => {
        this.setSizes();
        this._updateBlocSizes();
        this._updateBlocPositions();
        // set position to current bloc
        this.panner._translateTo({x: -this.currentBloc.position.x, y: -this.currentBloc.position.y});
        this.panner.pos = {x: -this.currentBloc.position.x, y: -this.currentBloc.position.y};
    }

    /**
     * set cell and bloc sizes
     */
    setSizes() {
        // TODO: add to wall params
        this.cellSize = {width: 210, height: 180};
        this.blocSize = {width: States.width - this.panelOffset.x, height: States.height - this.panelOffset.y};
    }

    /**
     * init wall events
     * @private
     */
    _initEvents() {
        window.addEventListener('resize', debounce(this._handleResize, 300));
    }

    /**
     * Init grid, create first bloc and side blocs arounds with cells
     * @private
     */
    _initBlocGrid() {
        this.panelOffset.y = this.dom.nav.clientHeight;
        this.currentBlocIndex = {x:0, y:0};
        this.currentBloc = this._createBlock(this.currentBlocIndex);
        this._updateBlocks(this.currentBlocIndex);
    }

    displayTutorial() {
        if(CookiesManager.check('wall_visited')) return;
        // Tutorial animation
        this.tutorialTimeline = new TimelineMax({
            paused:true,
            onComplete: () => {
                this.active = true;
            }
        });
        var dom = this.dom.tutorial;
        // Tutorial animation timeline
        this.tutorialTimeline.set(dom.container, {visibility: 'visible', opacity: 1})
            .fromTo(dom.iconCircle, 2.5, {scale: 2, opacity: 0,  transformOrigin:"50% 50%"}, {scale: 1, opacity: 1, transformOrigin:"50% 50%", ease: Power4.easeOut})
            .fromTo(dom.iconArrows, 2.5, {scale: 0.7, opacity: 0, transformOrigin:"50% 50%"}, {scale: 1, opacity: 1, transformOrigin:"50% 50%", ease: Power4.easeOut}, 0.9, 'arrows')
            .to(dom.icon, 0.8, {x: 60, ease: Power4.easeOut},  'arrows')
            .to(dom.icon, 2.4, {x: 0, ease: Back.easeOut.config(10)})
            .fromTo(dom.text, 2, {opacity: 0}, {opacity: 1, ease: Power3.easeOut}, 2)
            .to(dom.container, 0.5, {opacity: 0, ease: Power3.easeOut}, 'hide')
            .to(this.dom.overlay, 0.5, {opacity: 0, ease: Power3.easeOut}, 'hide')
            .play();

        // Set cookie for tutorial
        CookiesManager.set('wall_visited', 1);
    }

    /**
     * Update blocs from input node bloc
     * @param {Node} targetBloc
     * @private
     */
    _manageBlocGrid(targetBloc) {
        if(targetBloc.gridIndex === this.currentBlocIndex) return;
        const index = targetBloc.gridIndex;
        this._updateBlocks(index);
        this.currentBloc = this.blocList.get(this._stringifyIndex(index));
    }

    /**
     * Add sides blocs from bloc index, remove others and set current
     * @param {{x: Number, y: Number}} index Bloc index
     * @private
     */
    _updateBlocks(index) {
        // update current bloc
        this.currentBlocIndex = index;
        const sidesIndexes = this._getSideIndexes(index);

        // add blocs
        sidesIndexes.forEach(val => {
            const index = {x:val[0], y:val[1]};
            const mapIndex = this._stringifyIndex(index);

            // add bloc
            if(!this.blocList.has(mapIndex)) {
                this._createBlock(index);
            }
        });

        // remove blocs
        this.blocList.forEach((bloc, index) => {
            const isSide = sidesIndexes.filter(tempIndex => index === tempIndex.toString()).length <= 0;
            if(isSide && this._currentBlocMapIndex !== index) {
                this._removeBlock(bloc.index);
            }
        });


        // Status
        this.setStatus(`add blocs from ${index.x}, ${index.y}`);

        // Emit wall update
        this.emit('grid:update');
    }

    /**
     * Get side indexes of input index bloc
     * @param {{x: Number, y: Number}} index Bloc index
     * @returns {Array.<Array.<Number>>} Array of indexes
     */
    _getSideIndexes(index) {
        return [
            [index.x - 1, index.y - 1],
            [index.x, index.y - 1],
            [index.x + 1, index.y - 1],
            [index.x - 1, index.y],
            [index.x + 1, index.y],
            [index.x - 1, index.y + 1],
            [index.x, index.y + 1],
            [index.x + 1, index.y + 1],
        ]
    }

    /**
     * Create a wall bloc frol indexs
     * @param {{x: Number, y: Number}} index Bloc index
     * @returns {WallBloc} Created bloc
     */
    _createBlock(index) {
        // get index string for bloc map list
        const mapIndex = this._stringifyIndex(index);
        // randomise data
        const blocDatas = this._shuffleData();
        // create all bloc
        const bloc = new WallBloc({
            index: index,
            wall: this,
            datas: blocDatas
        });
        this.dom.panel.appendChild(bloc.dom.element);
        bloc.autoFlowItems();
        this.blocList.set(mapIndex, bloc);
        return bloc;
    }

    /**
     * Remove bloc with index
     * @param {{x: Number, y: Number}} index Bloc index
     * @private
     */
    _removeBlock(index) {
        const mapIndex = this._stringifyIndex(index);
        const bloc = this.blocList.get(mapIndex);
        this.dom.panel.removeChild(bloc.dom.element);
        this.blocList.delete(mapIndex);
    }

    /**
     * Update positions for all blocs in wall
     * @private
     */
    _updateBlocPositions() {
        this.blocList.forEach((bloc) => {
            bloc.setPosition();
        })
    }

    /**
     * Update sizes for all blocs in wall
     * @private
     */
    _updateBlocSizes() {
        this.blocList.forEach((bloc) => {
            bloc.updateSize();
        })
    }

    /**
     * Get templates in from this.dom.templates keys.
     * Note :Templates must be in wall page
     * @private
     */
    _loadTemplates () {
        for (var tpl in this.dom.templates) {
            if (!this.dom.templates.hasOwnProperty(tpl)) {
                continue;
            }

            this.dom.templates[tpl] = document.getElementById('template-' + tpl).innerHTML;
        }
    };

    /**
     * Shuffle wall data
     * @private
     */
    _shuffleData() {
        const datas = {};
        for (var key in this.datas) {
            if (!this.datas.hasOwnProperty(key)) continue;
            var array = arrayShuffle(this.datas[key]);
            datas[key] = array;
        }
        return datas;
    }

    /**
     * Format index x y to string for bloc reference in this.blocList
     * @param {{x: Number, y: Number}} index Input wall index
     * @return {string} String formated 'x.y'
     * @private
     */
    _stringifyIndex(index) {
        return `${index.x},${index.y}`;
    }

    /**
     * Add formated and colored console log for loggin wall status
     * @param {String} msg Message
     */
    setStatus(msg) {
        console.log('%c- WallManager - ' + msg, "color:#00CBB7");
    }
}

export {WallManager as default}


// WEBPACK FOOTER //
// ./src/js/pages/recipes/wall/WallManager/index.js